/**
 * The When pill's model (1.O-1 deliverable 7), pure: which placement the pill edits, and what it
 * reads. A session page's pill is its `session` placement; any other page's the first live canon
 * `occurs` placement by order key, then id; else the first `occurs`; else none.
 */
import { type CompiledCalendar, formatTimePoint } from '@codex/calendar';
import type { DatedPointV1, PlacementV1 } from '@codex/contracts';

const byOrder = (a: PlacementV1, b: PlacementV1) =>
  a.orderKey < b.orderKey ? -1 : a.orderKey > b.orderKey ? 1 : a.id < b.id ? -1 : 1;

export function primaryPlacement(
  placements: readonly PlacementV1[],
  typeKey: string | null,
): PlacementV1 | null {
  const sorted = [...placements].sort(byOrder);
  if (typeKey === 'session') {
    const session = sorted.find((p) => p.role === 'session');
    if (session) return session;
  }
  const occurs = sorted.filter((p) => p.role === 'occurs');
  return occurs.find((p) => p.effectiveStatus === 'canon') ?? occurs[0] ?? null;
}

const PROBLEMS = new Set(['dropped_cycle', 'contradiction', 'lag_unresolved']);

/** An order problem: one of its constraints failed, or a date's ticks are invalid. */
export function hasOrderProblem(placement: PlacementV1): boolean {
  const invalid = (p: DatedPointV1 | null) => p !== null && p.ticks.status === 'invalid';
  return (
    placement.constraints.some((c) => PROBLEMS.has(c.status)) ||
    invalid(placement.start) ||
    invalid(placement.end)
  );
}

/** A dated point in its canonical form; the stored label when the calendar cannot write it. */
export function pointText(calendar: CompiledCalendar | null, point: DatedPointV1): string {
  return (calendar && formatTimePoint(calendar, point.point)) || point.point.label || '?';
}

/** "9 Ember 1042 – 10 Ember 1042", "since …" for an ongoing span, or "Undated". */
export function placementDate(calendar: CompiledCalendar | null, p: PlacementV1): string {
  if (p.start === null) return 'Undated';
  const start = pointText(calendar, p.start);
  if (p.end !== null) return `${start} – ${pointText(calendar, p.end)}`;
  return p.ongoing ? `since ${start}` : start;
}

export interface PillText {
  readonly text: string;
  /** "+<n>" for the page's further placements, or null. */
  readonly more: string | null;
  readonly problem: boolean;
}

export function pillText(
  placements: readonly PlacementV1[],
  typeKey: string | null,
  calendar: CompiledCalendar | null,
): PillText {
  const primary = primaryPlacement(placements, typeKey);
  const others = placements.length - (primary === null ? 0 : 1);
  if (primary === null) {
    return { text: 'Add date', more: others > 0 ? `+${others}` : null, problem: false };
  }
  return {
    text: placementDate(calendar, primary),
    more: others > 0 ? `+${others}` : null,
    problem: hasOrderProblem(primary),
  };
}
